'use client';

import { memo, useMemo } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  MarkdownPreview,
  type MarkdownPreviewCustomProps,
} from './MarkdownPreview';
import { splitStreamingMarkdownBlocks } from './utils/split-streaming-markdown-blocks';

type FrozenMarkdownBlockProps = Omit<MarkdownPreviewCustomProps, 'source'> & {
  blockSource: string;
};

const FrozenMarkdownBlock = memo(
  function FrozenMarkdownBlock({
    blockSource,
    ...previewProps
  }: FrozenMarkdownBlockProps) {
    return <MarkdownPreview {...previewProps} source={blockSource} />;
  },
  (prev, next) =>
    prev.blockSource === next.blockSource &&
    prev.compact === next.compact &&
    prev.fontSize === next.fontSize &&
    prev.escapeRawHtml === next.escapeRawHtml &&
    prev.showCodeLineNumbers === next.showCodeLineNumbers &&
    prev.objectLinks === next.objectLinks &&
    prev.components === next.components,
);

export type StreamingMarkdownPreviewProps = MarkdownPreviewCustomProps & {
  isStreaming?: boolean;
};

export const StreamingMarkdownPreview = memo(function StreamingMarkdownPreview({
  source,
  isStreaming = false,
  ...previewProps
}: StreamingMarkdownPreviewProps) {
  const split = useMemo(
    () =>
      isStreaming
        ? splitStreamingMarkdownBlocks({ source: source ?? '' })
        : null,
    [isStreaming, source],
  );

  if (!isStreaming || !split || split.frozenBlocks.length === 0) {
    return <MarkdownPreview {...previewProps} source={source} />;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0} width="100%">
      {split.frozenBlocks.map((block, index) => (
        <FrozenMarkdownBlock
          key={`frozen-md-${index}`}
          blockSource={block}
          {...previewProps}
        />
      ))}
      {split.activeBlock.length > 0 ? (
        <MarkdownPreview {...previewProps} source={split.activeBlock} />
      ) : null}
    </Box>
  );
});
